import {
  FileExportIcon,
  FileImportIcon,
  FiltersToolbar,
  getFlowFilters,
  getFlowProperties,
  HeaderWithScrollables,
  IFlow,
  localStorageKeyWorkboardPageFiltersValue,
  localStorageKeyWorkboardPageProperties,
  makeDynamicWorkboardPageStore,
  Menu,
  PageLayout,
  PageNames,
  SyncFiltersPropertiesProvider,
  useEnvironment,
  useExportGridButton,
  useFilters,
  useGetFiltersToolbarHeight,
  useImportGridButton,
  useLocationTrackingForWorkboard,
  useParams,
  usePerformanceGraphQLExportParameters,
  useSidePeekDrawerProps,
  useSidePeekPreference,
  useTableSort,
  DownloadAllFlowWorksButton,
  WellKnownFlowFeaturesEnum,
} from '@diana/core';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BuilderButton } from './BuilderButton';
import { Content } from './Content';
import { HeaderLeft, HeaderRight } from './Headers';
import { WORKBOARD_PAGE_NS } from './i18n';
import { StartWorkButton } from './StartWorkButton';
import { createPropertyToFilter } from './Support';

export function WorkboardContainer({
  flow,
  dataGridStorageKey,
}: {
  flow?: IFlow;
  dataGridStorageKey: string;
}) {
  const { t } = useTranslation(WORKBOARD_PAGE_NS);
  const { flowId } = useParams<{ flowId: string }>();
  const flowOriginId = flow?.flowOriginId ?? '';
  const flowDisplayName = flow?.displayName ?? '';
  const isOfflineModeEnabled =
    flow?.featureFlags?.[WellKnownFlowFeaturesEnum.IsOfflineModeEnabled] === true;
  const gridApiRef = useGridApiRef();
  const environment = useEnvironment();
  const [exportImportButtonsAnchorElement, setExportImportButtonsAnchorElement] =
    useState<HTMLButtonElement | null>(null);

  const useWorkboardPageStore = makeDynamicWorkboardPageStore(flowOriginId);
  const exportConfigurations = useWorkboardPageStore((store) => store.exportConfigurations);
  const availableFilters = useWorkboardPageStore((store) => store.availableFilters);
  const filtersValues = useWorkboardPageStore((store) => store.filtersValues);
  const setFiltersValues = useWorkboardPageStore((store) => store.setFiltersValues);
  const filters = useWorkboardPageStore((store) => store.filters);
  const setFilters = useWorkboardPageStore((store) => store.setFilters);
  const setFlow = useWorkboardPageStore((store) => store.setFlow);
  const setAvailableFilters = useWorkboardPageStore((store) => store.setAvailableFilters);
  const resetData = useWorkboardPageStore((store) => store.resetData);
  const setDefaults = useWorkboardPageStore((store) => store.setDefaults);
  const defaultFiltersValuesAndPropertiesSet = useWorkboardPageStore(
    (store) => store.defaultFiltersValuesAndPropertiesSet,
  );

  const { setIsImportModalOpen, ImportModalComponent } = useImportGridButton(exportConfigurations);
  const sidePeekDrawerProps = useSidePeekDrawerProps();

  const { isLoadingUserSidePeekPreference, isUserSidePeekPreference } = useSidePeekPreference({
    flowOriginId,
  });

  const supportedProperties = useMemo(() => {
    return flow
      ? getFlowProperties({
          flow,
          onlyEnabled: false,
          onlyDefaultFilter: false,
        })
      : [];
  }, [flow]);

  const { sortModel, onSortModelChange } = useTableSort({ supportedProperties });

  const queryParameters = usePerformanceGraphQLExportParameters({
    flows: flow ? [flow] : [],
    filters: availableFilters,
    filtersValues: filtersValues,
    sortModel,
  });

  const { isExportModalOpen, setIsExportModalOpen, ExportModalComponent } = useExportGridButton({
    apiRef: gridApiRef,
    params: queryParameters,
    exportConfigurations,
  });

  const propertyToFilter = useMemo(createPropertyToFilter, []);

  useEffect(() => {
    if (!flow) return;
    setFlow(flow);
    setAvailableFilters(
      getFlowFilters({ flow, propertyToFilter, onlyEnabled: false, onlyDefaultFilter: false }),
    );
    // eslint-disable-next-line
  }, [flow]);

  useEffect(() => {
    return () => resetData();
    // eslint-disable-next-line
  }, []);

  const { track } = useLocationTrackingForWorkboard({ flow });
  useEffect(() => {
    if (flow) track();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow]);

  const { defaultData, onFilterChange, needToRenderAIFilter } = useFilters({
    flows: flow ? [flow] : [],
    createPropertyToFilter,
    filtersValues,
    setFiltersValues,
    availableFilters,
  });

  const height = useGetFiltersToolbarHeight(true);

  return (
    <SyncFiltersPropertiesProvider
      filtersKey={localStorageKeyWorkboardPageFiltersValue(environment, flowOriginId)}
      propertiesKey={localStorageKeyWorkboardPageProperties(environment, flowOriginId)}
      subscribe={useWorkboardPageStore.subscribe}
      storeSetDefaults={setDefaults}
      defaultFiltersValuesAndPropertiesSet={defaultFiltersValuesAndPropertiesSet || !flow}
    >
      <PageLayout
        name={PageNames.WORKBOARD}
        title={flowDisplayName}
        showOfflineError={false}
        isContentLoading={isLoadingUserSidePeekPreference}
        rightDrawer={sidePeekDrawerProps}
        filters={
          <FiltersToolbar
            availableFilters={availableFilters}
            setAvailableFilters={setAvailableFilters}
            filters={filters}
            setFilters={setFilters}
            filtersValues={filtersValues}
            setFiltersValues={setFiltersValues}
            onFilterChange={onFilterChange}
            defaultData={defaultData}
            needToRenderAIFilter={needToRenderAIFilter}
            height={height}
            flowOriginId={flowOriginId}
            isContentLoading={isLoadingUserSidePeekPreference}
          />
        }
        header={
          flow ? (
            <HeaderWithScrollables
              left={<HeaderLeft flow={flow} />}
              right={
                <Fragment>
                  {flowId && (
                    <StartWorkButton
                      flowId={flowId}
                      openInSidePeek={
                        isUserSidePeekPreference ??
                        flow?.featureFlags?.isSingleScreenWorkUpdate ??
                        true
                      }
                    />
                  )}
                  {!!flow && isOfflineModeEnabled && (
                    <DownloadAllFlowWorksButton
                      flowOriginId={flow.flowOriginId}
                      flowId={flow.id}
                      flowName={flowDisplayName}
                    />
                  )}
                  {!!flow && <BuilderButton flow={flow} />}
                  <HeaderRight
                    flow={flow}
                    handleCsvButtonClick={(event) => {
                      if (
                        environment.toLowerCase() === 'lupa' ||
                        environment.toLowerCase() === 'folium'
                      ) {
                        setExportImportButtonsAnchorElement(event.currentTarget);
                      } else {
                        setIsExportModalOpen(true);
                      }
                    }}
                  />
                </Fragment>
              }
            />
          ) : null
        }
      >
        {flow ? (
          <Fragment>
            <Content
              ref={gridApiRef}
              flow={flow}
              forceEmptySelection={isExportModalOpen}
              supportedProperties={supportedProperties}
              sortModel={sortModel}
              onSortModelChange={onSortModelChange}
              isUserSidePeekPreference={isUserSidePeekPreference}
              dataGridStorageKey={dataGridStorageKey}
            />
            {ExportModalComponent}
            {ImportModalComponent}
            <Menu
              open={Boolean(exportImportButtonsAnchorElement)}
              anchorEl={exportImportButtonsAnchorElement}
              onClose={() => setExportImportButtonsAnchorElement(null)}
              actions={[
                {
                  title: t('menu.import'),
                  onClick: () => {
                    setIsImportModalOpen(true);
                    setExportImportButtonsAnchorElement(null);
                  },
                  icon: <FileImportIcon sx={{ mr: '5px' }} />,
                },
                {
                  title: t('menu.export'),
                  onClick: () => {
                    setIsExportModalOpen(true);
                    setExportImportButtonsAnchorElement(null);
                  },
                  icon: <FileExportIcon sx={{ mr: '2px', ml: '3px' }} />,
                },
              ]}
              position="bottomRightInner"
            />
          </Fragment>
        ) : null}
      </PageLayout>
    </SyncFiltersPropertiesProvider>
  );
}
